/** Rows shown in the in-app table before the rest are left to the download. */
const MAX_PREVIEW_ROWS = 1000;

interface ParsedDelimitedText {
  /** Parsed rows (header first), capped at the requested row limit. */
  rows: string[][];
  /** Total number of rows in the file, including any beyond the cap. */
  totalRows: number;
}

/**
 * Parses CSV/TSV text following RFC 4180 quoting: fields may be wrapped in
 * double quotes, a doubled quote inside a quoted field is a literal quote, and
 * quoted fields may span lines. Parsing never throws; an unterminated quote
 * simply runs to the end of the text.
 *
 * @param {string} text - The file's text
 * @param {string} delimiter - The field separator (`,` for CSV, `\t` for TSV)
 * @param {number} [maxRows] - Rows to keep; later rows are counted but dropped
 * @returns {ParsedDelimitedText} The kept rows and the file's total row count
 */
function parseDelimitedText(text: string, delimiter: string, maxRows = MAX_PREVIEW_ROWS): ParsedDelimitedText {
  const rows: string[][] = [];
  let totalRows = 0;
  let row: string[] = [];
  let field = '';
  let inQuotes = false;
  let rowHasContent = false;

  const endRow = () => {
    row.push(field);
    if (rowHasContent || row.length > 1 || field !== '') {
      if (totalRows < maxRows) rows.push(row);
      totalRows += 1;
    }
    row = [];
    field = '';
    rowHasContent = false;
  };

  for (let index = 0; index < text.length; index += 1) {
    const character = text[index];
    if (inQuotes) {
      if (character === '"') {
        if (text[index + 1] === '"') {
          field += '"';
          index += 1;
        } else {
          inQuotes = false;
        }
      } else {
        field += character;
      }
      continue;
    }
    if (character === '"' && field === '') {
      inQuotes = true;
      rowHasContent = true;
    } else if (character === delimiter) {
      row.push(field);
      field = '';
      rowHasContent = true;
    } else if (character === '\n' || character === '\r') {
      if (character === '\r' && text[index + 1] === '\n') index += 1;
      endRow();
    } else {
      field += character;
    }
  }
  if (field !== '' || row.length > 0 || rowHasContent) endRow();

  return { rows, totalRows };
}

/**
 * Re-indents valid JSON with two spaces by rewriting only the whitespace
 * between tokens, so every key, string, and number keeps its exact spelling
 * (large integers, `1.0`, repeated keys) instead of round-tripping through
 * `JSON.parse`/`JSON.stringify`.
 *
 * @param {string} text - JSON text that is already known to be valid
 * @returns {string} The same tokens, two-space indented
 */
function reindentJson(text: string): string {
  let output = '';
  let depth = 0;
  const newline = () => `\n${'  '.repeat(depth)}`;
  for (let index = 0; index < text.length; index += 1) {
    const character = text[index];
    if (character === '"') {
      let end = index + 1;
      while (end < text.length && text[end] !== '"') {
        end += text[end] === '\\' ? 2 : 1;
      }
      output += text.slice(index, end + 1);
      index = end;
    } else if (character === '{' || character === '[') {
      const closing = character === '{' ? '}' : ']';
      let next = index + 1;
      while (next < text.length && /\s/.test(text[next])) next += 1;
      if (text[next] === closing) {
        output += character + closing;
        index = next;
      } else {
        depth += 1;
        output += character + newline();
      }
    } else if (character === '}' || character === ']') {
      depth -= 1;
      output += newline() + character;
    } else if (character === ',') {
      output += `,${newline()}`;
    } else if (character === ':') {
      output += ': ';
    } else if (!/\s/.test(character)) {
      output += character;
    }
  }
  return output;
}

interface FormattedJson {
  /** Pretty-printed JSON, or the original text when it does not parse. */
  text: string;
  /** Whether the text parsed as JSON. */
  valid: boolean;
}

/**
 * Pretty-prints JSON with two-space indentation without changing any value,
 * falling back to the original text so a malformed file is still readable.
 *
 * @param {string} text - The file's text
 * @returns {FormattedJson} The display text and whether it parsed
 */
function formatJson(text: string): FormattedJson {
  try {
    JSON.parse(text);
  } catch {
    return { text, valid: false };
  }
  return { text: reindentJson(text), valid: true };
}

export { formatJson, MAX_PREVIEW_ROWS, parseDelimitedText };
export type { FormattedJson, ParsedDelimitedText };
